import { confirmEvent, getEvevntInfoById, getEventResume } from "../../services/selectTimeService";
import { ACTION_PARAM, APPOINTMENT_ACTIONS, DROPOFF_RESUME_PARAM, EVENT_ID_PARAM, SCREEN } from "../../utils/constants";
import { primaryColor, setCurrentDateInUse, setEventPayload, setEventRoom, setLeadInfo, setLocationId, setProviderId, setSelectedAddons, setSelectedLocation, setSelectedProcedures, form, setAccessToken } from "../../utils/variables";
import { setAppointmentDuration, setAppointmentSummary, setEventId, setAppointmentChannelId } from "../../utils/variables";
import { loadAppointmentConfirmed } from "../../views/components/appointmentConfirmed";
import { loadAppointmentCancel } from "../../views/components/appointmentCancel";
import { toastError } from "../../views/components/error";
import { appointmentConfirmedListeners } from "../events/appointmentConfirmedListeners";
import { appointmentCancelListeners } from "../events/appointmentCancelListeners";
import { appointmentSummaryListeners } from "../events/appointmentSummaryListeners";
import { getClassScreen, navigate, params, setActionParam } from "../events/movePages";
import { selectOnlyTimeListeners } from "../events/selectTimeListeners";
import { loadOnlyTimeCarousel } from "../../views/components/timeSelect";
import moment from 'moment';
import { getProviderFullName } from "./providerActions";
import { loadAppointmentSummary } from "./selectTimeActions";

/**
 * Run the reschedule flow (select new time). Used when user clicks Reschedule on the confirmed page
 * or lands with action=reschedule in the URL.
 */
export async function runRescheduleFlow(eventData) {
    // Reset to the widget token so slot/fully-booked-days APIs work (Lead tokens lack staff.slots.show)
    if (form?.token) {
        setAccessToken(form.token);
    }
    const leadInfo = eventData?.participants?.find(p => p.participable_type === 'App\\Models\\Lead')?.participable;
    const provider = eventData?.participants?.find(p => p.participable_type === 'App\\Models\\Staff')?.participable;
    if (provider) setProviderId(provider.id);
    if (leadInfo) setLeadInfo(leadInfo);
    setSelectedProcedures(eventData.procedures?.map(p => p.id) ?? []);
    setSelectedAddons(eventData.procedure_addons ?? []);
    setSelectedLocation(eventData.location_id);
    const appointmentChannelIdFromEvent = eventData.appointment_channel_id ?? eventData.appointment_channel?.id;
    if (appointmentChannelIdFromEvent != null) setAppointmentChannelId(appointmentChannelIdFromEvent);
    setCurrentDateInUse(moment().unix());
    await loadOnlyTimeCarousel();
    selectOnlyTimeListeners();
    /* Set action=reschedule in URL so that when user picks a timeslot we show reschedule-reason and call reschedule API instead of create event. */
    setActionParam(APPOINTMENT_ACTIONS.RESCHEDULE);
    const titleEl = document.querySelector('.selectOnlyDate__container .title__content');
    if (titleEl) titleEl.innerHTML = `Select Time with ${getProviderFullName()}`;
    navigate(SCREEN.SELECT_ONLY_DATE);
}

export async function loadAppoinmentConfirmedLandingPage() {
    const eventId = params(EVENT_ID_PARAM);
    const signature = params('signature');
    const expires = params('expires');
    const useResumeLink = params(DROPOFF_RESUME_PARAM) === 'summary' && signature && expires;

    const eventInfoRes = useResumeLink
        ? await getEventResume(eventId, signature, expires)
        : await getEvevntInfoById(eventId);
    const eventData = eventInfoRes?.data ?? eventInfoRes;

    // Allow dropoff so resume links (dropoff_resume=summary) can load and let the lead continue booking
    if (!['request', 'confirmed', 'complete', 'cancelled', 'dropoff'].includes(eventData.status)) {
        toastError("Event has been cancelled.");
    } else {
        setEventPayload(eventData);
        setEventRoom(eventData.event_room);
        setLocationId(eventData.location_id);

        // Dropoff resume or event is in dropoff status: show summary so user can continue to deposit
        const isDropoffResume = params(DROPOFF_RESUME_PARAM) === 'summary' || eventData.status === 'dropoff';
        if (isDropoffResume) {
            const appointmentChannelId = eventData.appointment_channel_id || eventData.appointment_channel?.id;
            if (!appointmentChannelId) {
                toastError("Unable to load appointment summary.");
                return;
            }
            try {
                const confirmRes = await confirmEvent(eventId, appointmentChannelId);
                const lead = eventData?.participants?.find(p => p.participable_type === 'App\\Models\\Lead')?.participable;
                const provider = eventData?.participants?.find(p => p.participable_type === 'App\\Models\\Staff')?.participable;
                setLeadInfo(lead || null);
                setProviderId(provider?.id ?? null);
                setAppointmentSummary(confirmRes.data);
                setAppointmentDuration({ duration: confirmRes.data.duration || 0, requires_consult: confirmRes.data.requires_consult ?? false });
                setEventId(eventId);
                loadAppointmentSummary();
                return;
            } catch (e) {
                toastError(e?.response?.data?.message || "Unable to load appointment summary.");
                return;
            }
        }

        const action = params(ACTION_PARAM);
        if (action === APPOINTMENT_ACTIONS.CANCEL) {
            loadAppointmentCancel();
            appointmentCancelListeners();
            document.querySelector(getClassScreen(SCREEN.APPOINTMENT_CANCEL)).style.transition = 'none';
            navigate(SCREEN.APPOINTMENT_CANCEL);
        } else if (action === APPOINTMENT_ACTIONS.RESCHEDULE) {
            await runRescheduleFlow(eventData);
        } else {
            const actionSignature = params('signature');
            if (eventData.status === 'cancelled' && !actionSignature) {
                toastError("Event has been cancelled.");
                return;
            }
            loadAppointmentConfirmed();
            appointmentConfirmedListeners();
            document.querySelector(getClassScreen(SCREEN.APPOINTMENT_CONFIRMED)).style.transition = 'none';
            navigate(SCREEN.APPOINTMENT_CONFIRMED);
        }
    }
}

export function handleEventRoomNullUI() {
    const actionWrapperDom = document.querySelector('.appointment-confirmed__container .action');
    if (actionWrapperDom) actionWrapperDom.innerHTML = '';
}

export function handleEventRoomUI() {
    const actionWrapperDom = document.querySelector('.appointment-confirmed__container .action');
    if (actionWrapperDom) actionWrapperDom.innerHTML = '';
}
